import React, { useState } from 'react';
import { withTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import './features-help-guide.css';

export const FEATURES_HELP_OPEN_EVENT = 'ilocalbox:open-features-help';
export const FEATURES_TOUR_START_EVENT = 'ilocalbox:start-features-tour';

export function openFeaturesHelpGuide() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FEATURES_HELP_OPEN_EVENT));
  }
}

export function startFeaturesTour() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(FEATURES_TOUR_START_EVENT));
  }
}

const img = (name) =>
  (process.env.PUBLIC_URL || '') + '/help-guide/' + name;

const STEPS = [
  {
    key: 'catalog',
    tone: 'teal',
    image: 'help-guide-features.png',
    titleKey: 'FeaturesHelp.Step1Title',
    titleDefault: 'Browse the Features catalog',
    bodyKey: 'FeaturesHelp.Step1Body',
    bodyDefault:
      'Open Features in the left menu. Each card shows whether a licensed add-on is Enabled, Not enabled, or not available at this location. Search and filter to find the capability you need. Marketplace is the first add-on on this list.',
  },
  {
    key: 'talk',
    tone: 'orange',
    image: 'help-guide-talk-to-expert.png',
    titleKey: 'FeaturesHelp.Step2Title',
    titleDefault: 'Talk to an expert',
    bodyKey: 'FeaturesHelp.Step2Body',
    bodyDefault:
      'Open a locked feature and use Talk to Expert. Account and contact email are filled for you. Add a location and notes if you like. This does not charge anything in the portal — an iLocal specialist follows up, usually within one business day.',
  },
  {
    key: 'review',
    tone: 'purple',
    image: 'help-guide-approval.png',
    titleKey: 'FeaturesHelp.Step3Title',
    titleDefault: 'iLocal reviews and enables it',
    bodyKey: 'FeaturesHelp.Step3Body',
    bodyDefault:
      'iLocal management reviews the request offline. When the pharmacy is entitled, they turn the feature on in Product Licensing for the whole account. Empty location lists mean every location can use it. Geo rules can still block a specific kiosk.',
  },
  {
    key: 'notify',
    tone: 'green',
    image: 'help-guide-available.png',
    titleKey: 'FeaturesHelp.Step4Title',
    titleDefault: 'You are notified when it is available',
    bodyKey: 'FeaturesHelp.Step4Body',
    bodyDefault:
      'Pharmacy contacts receive an email when the feature is turned on. Features then shows Enabled. Nothing is billed from this screen — pricing and contracts stay with iLocal, outside the portal.',
  },
  {
    key: 'deliver',
    tone: 'coral',
    image: 'help-guide-deliver-otc.png',
    titleKey: 'FeaturesHelp.Step5Title',
    titleDefault: 'Use it on Deliver',
    bodyKey: 'FeaturesHelp.Step5Body',
    bodyDefault:
      'Once Marketplace is enabled, add items from Marketplace to the same locker pickup as a prescription. Stock products first, then assign from Deliver. If a location is blocked by local rules, a callout explains why.',
  },
];

export const FeaturesHelpGuide = ({ t, open, onClose, onStartTour }) => {
  const handleStartTour = () => {
    if (onClose) onClose();
    if (onStartTour) {
      onStartTour();
      return;
    }
    startFeaturesTour();
  };

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      className="features-help-dialog"
      style={{ zIndex: 1400 }}
      PaperProps={{
        className: 'features-help-paper',
      }}
    >
      <div className="features-help-hero">
        <IconButton
          aria-label={t('Tour.Close', { defaultValue: 'Close' })}
          onClick={onClose}
          className="features-help-close"
        >
          <CloseIcon />
        </IconButton>
        <p className="features-help-eyebrow">
          {t('FeaturesHelp.Eyebrow', { defaultValue: 'Licensed add-ons' })}
        </p>
        <h2>
          {t('FeaturesHelp.Title', {
            defaultValue: 'Features & Product Licensing guide',
          })}
        </h2>
        <p>
          {t('FeaturesHelp.Subtitle', {
            defaultValue:
              'How pharmacies request add-ons, how iLocal enables them offline, and how Marketplace shows up on Deliver.',
          })}
        </p>
      </div>
      <DialogContent className="features-help-body">
        {STEPS.map((step, index) => (
          <section
            key={step.key}
            className={`features-help-step is-${step.tone}`}
          >
            <div className="features-help-step-copy">
              <span className="features-help-step-num">{index + 1}</span>
              <h3>{t(step.titleKey, { defaultValue: step.titleDefault })}</h3>
              <p>{t(step.bodyKey, { defaultValue: step.bodyDefault })}</p>
            </div>
            <div className="features-help-step-media">
              <img
                src={img(step.image)}
                alt={t(step.titleKey, { defaultValue: step.titleDefault })}
              />
            </div>
          </section>
        ))}
        <div className="features-help-tips">
          <h3>
            {t('FeaturesHelp.TipsTitle', { defaultValue: 'Good to know' })}
          </h3>
          <ul>
            <li>
              {t('FeaturesHelp.Tip1', {
                defaultValue:
                  'Enablement is per pharmacy account, not per kiosk. After it is on, every eligible location can use it.',
              })}
            </li>
            <li>
              {t('FeaturesHelp.Tip2', {
                defaultValue:
                  'The portal never collects payment for add-ons. Talk to Expert starts a conversation; iLocal turns the feature on after the offline sale.',
              })}
            </li>
            <li>
              {t('FeaturesHelp.Tip3', {
                defaultValue:
                  'iLocal members use Product Licensing to enable or disable a feature, Feature Catalog to edit copy, and Feature Purchases to review requests.',
              })}
            </li>
            <li>
              {t('FeaturesHelp.Tip4', {
                defaultValue:
                  'A short in-app tour is also available from Help & Tours whenever you want a walkthrough of the screens.',
              })}
            </li>
          </ul>
        </div>
      </DialogContent>
      <DialogActions className="features-help-actions">
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('Tour.Close', { defaultValue: 'Close' })}
        </Button>
        <Button
          variant="contained"
          startIcon={<PlayArrowIcon />}
          onClick={handleStartTour}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #00a7e1 0%, #0077a3 100%)',
            boxShadow: '0 2px 8px rgba(0, 167, 225, 0.35)',
            '&:hover': { background: '#0096cc' },
          }}
        >
          {t('FeaturesHelp.StartTour', { defaultValue: 'Take the Features tour' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const FeaturesHelpButton = ({ t, onStartTour }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="features-help-trigger">
      <Button
        variant="outlined"
        size="small"
        startIcon={<HelpOutlineIcon />}
        onClick={() => setOpen(true)}
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          borderColor: '#7dd3fc',
          color: '#0369a1',
          background: '#fff',
          '&:hover': {
            borderColor: '#00a7e1',
            background: 'rgba(0, 167, 225, 0.06)',
          },
        }}
      >
        {t('FeaturesHelp.OpenGuide', { defaultValue: 'Help guide' })}
      </Button>
      <FeaturesHelpGuide
        t={t}
        open={open}
        onClose={() => setOpen(false)}
        onStartTour={onStartTour}
      />
    </div>
  );
};

export default withTranslation()(FeaturesHelpGuide);



// WEBPACK FOOTER //
// ./src/components/features/FeaturesHelpGuide.js